import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AudioProvider } from "@/contexts/AudioContext";
import { ThemeProvider } from "next-themes";
import { Suspense } from "react";
import { lazyWithReload } from "@/lib/lazyWithReload";

import PageTransition from "@/components/PageTransition";
import ChunkErrorBoundary from "@/components/ChunkErrorBoundary";
import Index from "./pages/Index";

// Lazy load non-critical pages (auto-recovers from stale deploy caches)
const NotFound = lazyWithReload(() => import("./pages/NotFound"));
const Admin = lazyWithReload(() => import("./pages/Admin"));
const Scanner = lazyWithReload(() => import("./pages/Scanner"));
const Terms = lazyWithReload(() => import("./pages/Terms"));
const ConfirmSubscription = lazyWithReload(() => import("./pages/ConfirmSubscription"));
const Tickets = lazyWithReload(() => import("./pages/Tickets"));

// Optimized QueryClient with better defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <PageTransition key={location.pathname}>
      <Suspense fallback={<div className="min-h-screen bg-background" />}>
        <Routes location={location}>
          <Route path="/" element={<Index />} />
          <Route path="/tickets" element={<Tickets />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/scanner" element={<Scanner />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/api/confirm" element={<ConfirmSubscription />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </PageTransition>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <LanguageProvider>
        <AudioProvider>
          <TooltipProvider delayDuration={300}>
            <Toaster />
            <Sonner position="top-center" closeButton richColors />
            <ChunkErrorBoundary>
              <BrowserRouter>
                <AnimatedRoutes />
              </BrowserRouter>
            </ChunkErrorBoundary>
          </TooltipProvider>
        </AudioProvider>
      </LanguageProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
